import { EoaGradingConfigurationPayload } from '../../services/eoaConfigServices';
import { decimalNumberError, letterGradeError, wholeNumberError } from './fieldValidation';
import createFormRowId from './formRowId';
import persistNestedConfiguration from './persistNestedConfiguration';

export type GradingSystem = '' | 'NUMERIC' | 'LETTER' | 'BOTH';

export interface GradeMapRow {
  id: string;
  letter: string;
  number: string;
}

export interface GradingForm {
  defaultCredits: string;
  gradingSystem: GradingSystem;
  failingNumber: string;
  failingLetter: string;
  carnegieUnits: string;
  gradeMaps: GradeMapRow[];
  multipleCreditSystems: string;
}

const GRADING_SYSTEM_OPTIONS: { value: Exclude<GradingSystem, ''>; label: string }[] = [
  { value: 'NUMERIC', label: 'Numeric' },
  { value: 'LETTER', label: 'Letter' },
  { value: 'BOTH', label: 'Both' },
];

const emptyGradeMap = (): GradeMapRow => ({ id: createFormRowId('grade'), letter: '', number: '' });

const INITIAL_GRADING_FORM: GradingForm = {
  defaultCredits: '',
  gradingSystem: '',
  failingNumber: '',
  failingLetter: '',
  carnegieUnits: '',
  gradeMaps: [emptyGradeMap()],
  multipleCreditSystems: '',
};

const emptyToNull = (value: string): string | null => value.trim() || null;

const parseOptionalNumber = (value: string): number | null => {
  const trimmed = value.trim();
  if (trimmed === '') return null;
  const parsed = Number(trimmed);
  return Number.isFinite(parsed) ? parsed : null;
};

const setFailingNumber = (form: GradingForm, value: string): GradingForm => ({ ...form, failingNumber: value });

const setFailingLetter = (form: GradingForm, value: string): GradingForm => ({ ...form, failingLetter: value });

const addGradeMap = (form: GradingForm): GradingForm => ({
  ...form,
  gradeMaps: [...form.gradeMaps, emptyGradeMap()],
});

const removeGradeMap = (form: GradingForm, id: string): GradingForm => ({
  ...form,
  gradeMaps: form.gradeMaps.filter((row) => row.id !== id),
});

const updateGradeMap = (form: GradingForm, id: string, field: 'letter' | 'number', value: string): GradingForm => ({
  ...form,
  gradeMaps: form.gradeMaps.map((row) => (row.id === id ? { ...row, [field]: value } : row)),
});

const areGradeMapsSaveable = (form: GradingForm): boolean =>
  form.gradeMaps.every((row) => letterGradeError(row.letter) === null && decimalNumberError(row.number) === null);

const toLetterNumberMap = (rows: GradeMapRow[]): Record<string, number> => {
  const map: Record<string, number> = {};
  rows.forEach((row) => {
    if (letterGradeError(row.letter) !== null || decimalNumberError(row.number) !== null) return;
    const letter = row.letter.trim();
    const number = parseOptionalNumber(row.number);
    if (!letter || number === null) return;
    map[letter] = number;
  });
  return map;
};

const toGradingConfigurationPayload = (form: GradingForm): EoaGradingConfigurationPayload => {
  const letterToNumber = toLetterNumberMap(form.gradeMaps);
  return {
    default_number_of_credits: emptyToNull(form.defaultCredits),
    grading_system: form.gradingSystem || null,
    failing_grade_numeric_threshold:
      wholeNumberError(form.failingNumber) === null ? parseOptionalNumber(form.failingNumber) : null,
    failing_grade_letter_threshold:
      letterGradeError(form.failingLetter) === null ? emptyToNull(form.failingLetter) : null,
    credit_hours_map_to_carnegie: emptyToNull(form.carnegieUnits),
    letter_to_number_grade_map: Object.keys(letterToNumber).length > 0 ? letterToNumber : {},
    multiple_credit_systems_in_transcripts: emptyToNull(form.multipleCreditSystems),
  };
};

const changedGradingPayload = (previous: GradingForm, next: GradingForm): EoaGradingConfigurationPayload => {
  const previousPayload = toGradingConfigurationPayload(previous);
  const nextPayload = toGradingConfigurationPayload(next);
  const patch: EoaGradingConfigurationPayload = {};

  if (previousPayload.default_number_of_credits !== nextPayload.default_number_of_credits) {
    patch.default_number_of_credits = nextPayload.default_number_of_credits;
  }
  if (previousPayload.grading_system !== nextPayload.grading_system) {
    patch.grading_system = nextPayload.grading_system;
  }
  if (
    wholeNumberError(next.failingNumber) === null &&
    previousPayload.failing_grade_numeric_threshold !== nextPayload.failing_grade_numeric_threshold
  ) {
    patch.failing_grade_numeric_threshold = nextPayload.failing_grade_numeric_threshold;
  }
  if (
    letterGradeError(next.failingLetter) === null &&
    previousPayload.failing_grade_letter_threshold !== nextPayload.failing_grade_letter_threshold
  ) {
    patch.failing_grade_letter_threshold = nextPayload.failing_grade_letter_threshold;
  }
  if (previousPayload.credit_hours_map_to_carnegie !== nextPayload.credit_hours_map_to_carnegie) {
    patch.credit_hours_map_to_carnegie = nextPayload.credit_hours_map_to_carnegie;
  }
  if (
    areGradeMapsSaveable(next) &&
    JSON.stringify(previousPayload.letter_to_number_grade_map) !==
      JSON.stringify(nextPayload.letter_to_number_grade_map)
  ) {
    patch.letter_to_number_grade_map = nextPayload.letter_to_number_grade_map;
  }
  if (previousPayload.multiple_credit_systems_in_transcripts !== nextPayload.multiple_credit_systems_in_transcripts) {
    patch.multiple_credit_systems_in_transcripts = nextPayload.multiple_credit_systems_in_transcripts;
  }

  return patch;
};

const hasInvalidGrading = (form: GradingForm): boolean =>
  wholeNumberError(form.failingNumber) !== null ||
  letterGradeError(form.failingLetter) !== null ||
  !areGradeMapsSaveable(form);

const hasGradingContent = (form: GradingForm): boolean => {
  const payload = toGradingConfigurationPayload(form);
  return (
    payload.default_number_of_credits !== null ||
    payload.grading_system !== null ||
    payload.failing_grade_numeric_threshold !== null ||
    payload.failing_grade_letter_threshold !== null ||
    payload.credit_hours_map_to_carnegie !== null ||
    Object.keys(payload.letter_to_number_grade_map ?? {}).length > 0 ||
    payload.multiple_credit_systems_in_transcripts !== null
  );
};

const isGradingSystem = (value: string | null | undefined): value is Exclude<GradingSystem, ''> =>
  value === 'NUMERIC' || value === 'LETTER' || value === 'BOTH';

const gradingFromEoa = (data: Partial<EoaGradingConfigurationPayload>): GradingForm => {
  const gradeMaps = Object.entries(data.letter_to_number_grade_map ?? {}).map(([letter, number]) => ({
    id: createFormRowId('grade'),
    letter,
    number: String(number),
  }));

  return {
    defaultCredits: data.default_number_of_credits ?? '',
    gradingSystem: isGradingSystem(data.grading_system) ? data.grading_system : '',
    failingNumber:
      data.failing_grade_numeric_threshold === undefined || data.failing_grade_numeric_threshold === null
        ? ''
        : String(data.failing_grade_numeric_threshold),
    failingLetter: data.failing_grade_letter_threshold ?? '',
    carnegieUnits: data.credit_hours_map_to_carnegie ?? '',
    gradeMaps: gradeMaps.length > 0 ? gradeMaps : [emptyGradeMap()],
    multipleCreditSystems: data.multiple_credit_systems_in_transcripts ?? '',
  };
};

const persistGrading = async ({
  form,
  previousForm,
  eoaId,
  exists,
  createGrading,
  patchGrading,
  createDraft,
}: {
  form: GradingForm;
  previousForm: GradingForm;
  eoaId: string | null;
  exists: boolean;
  createGrading: (eoaId: string, payload: EoaGradingConfigurationPayload) => Promise<unknown>;
  patchGrading: (eoaId: string, payload: EoaGradingConfigurationPayload) => Promise<unknown>;
  createDraft?: () => Promise<string | null>;
}): Promise<boolean> =>
  persistNestedConfiguration({
    form,
    previousForm,
    eoaId,
    exists,
    initialForm: INITIAL_GRADING_FORM,
    hasContent: hasGradingContent,
    changedPayload: changedGradingPayload,
    create: createGrading,
    patch: patchGrading,
    createDraft,
  });

export {
  GRADING_SYSTEM_OPTIONS,
  INITIAL_GRADING_FORM,
  addGradeMap,
  changedGradingPayload,
  gradingFromEoa,
  hasGradingContent,
  hasInvalidGrading,
  persistGrading,
  removeGradeMap,
  setFailingLetter,
  setFailingNumber,
  toGradingConfigurationPayload,
  updateGradeMap,
};
